import React, { useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AppSidebar from "@/components/AppSidebar";
import Dashboard from "@/pages/Dashboard";
import CRMPage from "@/pages/CRMPage";
import InvoicePage from "@/pages/InvoicePage";
import ImportPage from "@/pages/ImportPage";
import StockPage from "@/pages/StockPage";
import CalendarPage from "@/pages/CalendarPage";
import MessagesPage from "@/pages/MessagesPage";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_78be41ce-e0ba-44a5-83d7-3e82a17452bb/artifacts/xorqbqrr_Captura%20de%20pantalla%202026-02-04%20a%20las%2021.59.22.png";

// Initial app state - all data in memory
const initialState = {
  clients: [
    { id: 1, name: "Carlos Martínez", phone: "+34 612 345 678", budget: "40.000-50.000€", interest: "BMW Serie 3", status: "lead", notes: "Interesado en diesel automático", createdAt: "2026-02-01" },
    { id: 2, name: "María García", phone: "+34 623 456 789", budget: "60.000-80.000€", interest: "Mercedes GLC", status: "contact", notes: "Busca SUV familiar", createdAt: "2026-01-28" },
    { id: 3, name: "Antonio López", phone: "+34 634 567 890", budget: "50.000-60.000€", interest: "Audi A4", status: "options", notes: "Enviadas 3 opciones de Alemania", createdAt: "2026-01-20" },
    { id: 4, name: "Laura Sánchez", phone: "+34 645 678 901", budget: "70.000€", interest: "BMW X3", status: "reservation", notes: "Reserva confirmada, pendiente pago señal", createdAt: "2026-01-15" },
    { id: 5, name: "Pedro Ruiz", phone: "+34 656 789 012", budget: "55.000€", interest: "Audi Q5", status: "importing", notes: "Vehículo en transporte desde Múnich", createdAt: "2026-01-10" },
  ],
  vehicles: [
    { id: 1, brand: "BMW", model: "Serie 5 530d", year: 2024, cost: 42000, price: 52000, status: "available", clientId: null },
    { id: 2, brand: "Mercedes", model: "GLC 300", year: 2023, cost: 48000, price: 58000, status: "reserved", clientId: 4 },
    { id: 3, brand: "Audi", model: "Q5 45 TFSI", year: 2024, cost: 44000, price: 55000, status: "importing", clientId: 5 },
  ],
  imports: [
    { id: 1, vehicleId: 3, clientId: 5, status: "transport", timeline: [
      { stage: "selection", date: "2026-01-10", completed: true },
      { stage: "reservation", date: "2026-01-12", completed: true },
      { stage: "purchase", date: "2026-01-15", completed: true },
      { stage: "transport", date: "2026-01-20", completed: false },
      { stage: "documentation", date: null, completed: false },
      { stage: "delivery", date: null, completed: false },
    ], notes: "Transporte por carretera, ETA 5 días" }
  ],
  events: [
    { id: 1, title: "Llamar a Carlos Martínez", date: "2026-02-06", type: "call", clientId: 1 },
    { id: 2, title: "Entrega Audi Q5 - Pedro Ruiz", date: "2026-02-10", type: "delivery", clientId: 5 },
    { id: 3, title: "Seguimiento María García", date: "2026-02-07", type: "followup", clientId: 2 },
  ]
};

function App() {
  var _useState = useState(initialState);
  var appState = _useState[0];
  var setAppState = _useState[1];

  var _useState2 = useState("dashboard");
  var currentPage = _useState2[0];
  var setCurrentPage = _useState2[1];

  // Client functions
  var addClient = useCallback(function(client) {
    setAppState(function(prev) {
      var newId = Math.max(...prev.clients.map(function(c) { return c.id; }), 0) + 1;
      return { ...prev, clients: [...prev.clients, { ...client, id: newId, createdAt: new Date().toISOString().split('T')[0] }] };
    });
  }, []);

  var updateClient = useCallback(function(id, updates) {
    setAppState(function(prev) {
      return { ...prev, clients: prev.clients.map(function(c) { return c.id === id ? { ...c, ...updates } : c; }) };
    });
  }, []);

  var deleteClient = useCallback(function(id) {
    setAppState(function(prev) {
      return { ...prev, clients: prev.clients.filter(function(c) { return c.id !== id; }) };
    });
  }, []);

  // Vehicle functions
  var addVehicle = useCallback(function(vehicle) {
    setAppState(function(prev) {
      var newId = Math.max(...prev.vehicles.map(function(v) { return v.id; }), 0) + 1;
      return { ...prev, vehicles: [...prev.vehicles, { ...vehicle, id: newId }] };
    });
  }, []);

  var updateVehicle = useCallback(function(id, updates) {
    setAppState(function(prev) {
      return { ...prev, vehicles: prev.vehicles.map(function(v) { return v.id === id ? { ...v, ...updates } : v; }) };
    });
  }, []);

  // Event functions
  var addEvent = useCallback(function(event) {
    setAppState(function(prev) {
      var newId = Math.max(...prev.events.map(function(e) { return e.id; }), 0) + 1;
      return { ...prev, events: [...prev.events, { ...event, id: newId }] };
    });
  }, []);

  // Stats for dashboard
  var stats = {
    totalClients: appState.clients.length,
    activeLeads: appState.clients.filter(function(c) { return c.status === "lead" || c.status === "contact"; }).length,
    inProgress: appState.clients.filter(function(c) { return c.status === "options" || c.status === "reservation" || c.status === "importing"; }).length,
    delivered: appState.clients.filter(function(c) { return c.status === "delivered"; }).length,
    vehiclesAvailable: appState.vehicles.filter(function(v) { return v.status === "available"; }).length,
    vehiclesImporting: appState.vehicles.filter(function(v) { return v.status === "importing"; }).length,
    pendingEvents: appState.events.filter(function(e) { return new Date(e.date) >= new Date(); }).length
  };

  return (
    <BrowserRouter>
      <div className="flex min-h-screen bg-slate-100" data-testid="app">
        <AppSidebar 
          currentPage={currentPage} 
          onNavigate={setCurrentPage} 
          logoUrl={LOGO_URL}
          stats={stats}
        />
        
        <main className="flex-1 overflow-hidden">
          {currentPage === "dashboard" && (
            <Dashboard 
              stats={stats} 
              clients={appState.clients} 
              events={appState.events}
              onNavigate={setCurrentPage}
            />
          )}
          {currentPage === "crm" && (
            <CRMPage 
              clients={appState.clients}
              onAddClient={addClient}
              onUpdateClient={updateClient}
              onDeleteClient={deleteClient}
            />
          )}
          {currentPage === "invoices" && (
            <InvoicePage logoUrl={LOGO_URL} clients={appState.clients} vehicles={appState.vehicles} />
          )}
          {currentPage === "imports" && (
            <ImportPage imports={appState.imports} vehicles={appState.vehicles} clients={appState.clients} />
          )}
          {currentPage === "stock" && (
            <StockPage vehicles={appState.vehicles} onAddVehicle={addVehicle} onUpdateVehicle={updateVehicle} />
          )}
          {currentPage === "calendar" && (
            <CalendarPage events={appState.events} clients={appState.clients} onAddEvent={addEvent} />
          )}
          {currentPage === "messages" && (
            <MessagesPage clients={appState.clients} />
          )}
        </main>
        
        <Toaster position="top-right" />
      </div>
    </BrowserRouter>
  );
}

export default App;
